import React, { useState } from 'react';
import { StatsBar } from '../components/StatsBar';
import { CampaignCard, Campaign } from '../components/CampaignCard';
import { LeadListTable, Lead } from '../components/LeadListTable';
import { RuleEditorModal } from '../components/RuleEditorModal';
import { Rule } from '../components/QualificationRules';
import { LayoutDashboardIcon, FilterIcon } from 'lucide-react';
// Mock Data
const INITIAL_CAMPAIGNS: Campaign[] = [{
  id: 'CMP-2024-001',
  name: 'High Intent Refinance',
  status: 'active',
  leadsCount: 1240,
  contactRate: 22.5,
  healthScore: 85,
  rules: [{
    field: 'Credit Score',
    operator: '>=',
    value: '720',
    logic: 'AND'
  }, {
    field: 'LTV',
    operator: '<',
    value: '80%',
    logic: 'AND'
  }, {
    field: 'Lead Source',
    operator: '=',
    value: 'Organic',
    logic: 'AND'
  }]
}, {
  id: 'CMP-2024-002',
  name: 'FHA First Time Buyers',
  status: 'active',
  leadsCount: 856,
  contactRate: 15.2,
  healthScore: 62,
  rules: [{
    field: 'Loan Type',
    operator: '=',
    value: 'FHA',
    logic: 'AND'
  }, {
    field: 'Income',
    operator: '>=',
    value: '$60k',
    logic: 'OR'
  }, {
    field: 'Co-Borrower',
    operator: '=',
    value: 'Yes'
  }]
}, {
  id: 'CMP-2024-003',
  name: 'VA Streamline (IRRRL)',
  status: 'paused',
  leadsCount: 432,
  contactRate: 28.4,
  healthScore: 45,
  rules: [{
    field: 'Veteran Status',
    operator: '=',
    value: 'Yes',
    logic: 'AND'
  }, {
    field: 'Current Rate',
    operator: '>',
    value: '6.5%',
    logic: 'AND'
  }]
}];
const MOCK_LEADS: Record<string, Lead[]> = {
  'CMP-2024-001': [{
    id: 'LD-9921',
    name: 'Sarah Jenkins',
    phone: '(555) 123-4567',
    email: 'sarah.j@example.com',
    status: 'New',
    lastContact: 'Never',
    loanAmount: '$450,000',
    creditScore: 745,
    source: 'Web Form'
  }, {
    id: 'LD-9922',
    name: 'Michael Ross',
    phone: '(555) 987-6543',
    email: 'mross@example.com',
    status: 'Contacted',
    lastContact: '2 hours ago',
    loanAmount: '$320,000',
    creditScore: 728,
    source: 'Referral'
  }, {
    id: 'LD-9923',
    name: 'David Chen',
    phone: '(555) 456-7890',
    email: 'dchen@example.com',
    status: 'Qualified',
    lastContact: 'Yesterday',
    loanAmount: '$580,000',
    creditScore: 780,
    source: 'Organic'
  }, {
    id: 'LD-9924',
    name: 'Emily White',
    phone: '(555) 222-3333',
    email: 'ewhite@example.com',
    status: 'New',
    lastContact: 'Never',
    loanAmount: '$410,000',
    creditScore: 735,
    source: 'Web Form'
  }, {
    id: 'LD-9925',
    name: 'Robert Taylor',
    phone: '(555) 444-5555',
    email: 'rtaylor@example.com',
    status: 'Bad Number',
    lastContact: '3 days ago',
    loanAmount: '$290,000',
    creditScore: 710,
    source: 'Aggregator'
  }],
  'CMP-2024-002': [{
    id: 'LD-8801',
    name: 'Jessica Wu',
    phone: '(555) 111-2222',
    email: 'jwu@example.com',
    status: 'New',
    lastContact: 'Never',
    loanAmount: '$350,000',
    creditScore: 680,
    source: 'Zillow'
  }, {
    id: 'LD-8802',
    name: 'Tom Baker',
    phone: '(555) 333-4444',
    email: 'tbaker@example.com',
    status: 'Contacted',
    lastContact: '1 day ago',
    loanAmount: '$280,000',
    creditScore: 695,
    source: 'Trulia'
  }],
  'CMP-2024-003': [{
    id: 'LD-7701',
    name: 'James Wilson',
    phone: '(555) 777-8888',
    email: 'jwilson@example.com',
    status: 'Qualified',
    lastContact: 'Today',
    loanAmount: '$420,000',
    creditScore: 720,
    source: 'Direct Mail'
  }]
};
export function DialerCampaignDashboard() {
  const [campaigns, setCampaigns] = useState<Campaign[]>(INITIAL_CAMPAIGNS);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(INITIAL_CAMPAIGNS[0].id);
  // Rule Editor State
  const [isRuleEditorOpen, setIsRuleEditorOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const handleCampaignSelect = (id: string) => {
    setSelectedCampaignId(id);
    // Scroll to table on mobile
    if (window.innerWidth < 768) {
      const element = document.getElementById('leads-table');
      element?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleEditRules = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setIsRuleEditorOpen(true);
  };
  const handleSaveRules = (campaignId: string, newRules: Rule[]) => {
    setCampaigns(prev => prev.map(c => c.id === campaignId ? {
      ...c,
      rules: newRules
    } : c));
  };
  const selectedCampaign = campaigns.find(c => c.id === selectedCampaignId);
  const currentLeads = selectedCampaignId ? MOCK_LEADS[selectedCampaignId] || [] : [];
  return <div className="min-h-screen bg-gray-50/50 flex flex-col font-sans" data-id="element-188">
      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full" data-id="element-189">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-190">
          <div data-id="element-191">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-192">
              Campaign Overview
            </h2>
            <p className="text-gray-500 mt-1 text-sm" data-id="element-193">
              Monitor your dialer performance and lead qualification rules.
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-194">
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-195">
              <FilterIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-196" />
              Filters
            </button>
            <button className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-197">
              New Campaign
            </button>
          </div>
        </div>

        <StatsBar data-id="element-198" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-199">
          {/* Campaign List Column */}
          <div className="lg:col-span-4 space-y-6" data-id="element-200">
            <div className="flex items-center justify-between px-1" data-id="element-201">
              <h3 className="text-lg font-bold text-gray-900 tracking-tight" data-id="element-202">
                Active Campaigns
              </h3>
              <button className="text-sm text-blue-600 font-medium hover:text-blue-800 hover:underline" data-id="element-203">
                View All
              </button>
            </div>

            <div className="space-y-4" data-id="element-204">
              {campaigns.map(campaign => <CampaignCard key={campaign.id} campaign={campaign} onViewLeads={handleCampaignSelect} onEditRules={handleEditRules} isSelected={selectedCampaignId === campaign.id} data-id="element-205" />)}
            </div>
          </div>

          {/* Leads Data Column */}
          <div className="lg:col-span-8 sticky top-24" id="leads-table" data-id="element-206">
            {selectedCampaign ? <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 h-[calc(100vh-12rem)] min-h-[500px]" data-id="element-207">
                <LeadListTable leads={currentLeads} campaignName={selectedCampaign.name} data-id="element-208" />
              </div> : <div className="h-96 flex items-center justify-center bg-white rounded-xl border border-gray-200 border-dashed p-12 text-center" data-id="element-209">
                <div data-id="element-210">
                  <div className="mx-auto h-12 w-12 text-gray-300 bg-gray-50 rounded-full flex items-center justify-center mb-4" data-id="element-211">
                    <LayoutDashboardIcon className="w-6 h-6" data-id="element-212" />
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-213">
                    No campaign selected
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-214">
                    Select a campaign from the left to view its leads.
                  </p>
                </div>
              </div>}
          </div>
        </div>
      </main>

      {/* Rule Editor Modal */}
      {editingCampaign && <RuleEditorModal isOpen={isRuleEditorOpen} onClose={() => setIsRuleEditorOpen(false)} campaign={editingCampaign} onSave={handleSaveRules} data-id="element-215" />}
    </div>;
}